import type { CuratedContentItem } from "@/types/content";
import { SHOW_INFO, CATEGORY_PILL_COLORS } from "@/data/curatedContent";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight } from "lucide-react";

const EFFORT_LABELS: Record<string, { label: string; className: string }> = {
  Low: { label: "Easy Win", className: "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30" },
  Medium: { label: "Medium Effort", className: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30" },
  High: { label: "Deep Dive", className: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30" },
};

interface FeaturedArticleProps {
  item: CuratedContentItem;
  onClick: () => void;
  onBuild: () => void;
}

export function FeaturedArticle({ item, onClick, onBuild }: FeaturedArticleProps) {
  const show = SHOW_INFO[item.show];
  const pill = CATEGORY_PILL_COLORS[item.category];
  const effortStyle = EFFORT_LABELS[item.effort];

  return (
    <div
      className="mx-auto"
      style={{ maxWidth: 1000, marginBottom: 80 }}
    >
      <div className="flex flex-col md:flex-row" style={{ gap: 48 }}>
        {/* Left: Image */}
        <div
          role="button"
          tabIndex={0}
          onClick={onClick}
          onKeyDown={(e) => e.key === "Enter" && onClick()}
          className="cursor-pointer shrink-0"
          style={{ width: "100%", maxWidth: 480 }}
        >
          <div className="aspect-video w-full overflow-hidden bg-muted" style={{ borderRadius: 8 }}>
            <img
              src={item.imageUrl}
              alt={item.title}
              className="w-full h-full object-contain"
              loading="lazy"
            />
          </div>
        </div>

        {/* Right: Content */}
        <div style={{ maxWidth: 472 }} className="flex flex-col justify-center">
          {/* Show + creator at top */}
          <div
            className="flex items-center gap-1.5 text-muted-foreground"
            style={{ fontSize: 13, lineHeight: "18px", marginBottom: 10 }}
          >
            {show && (
              <img src={show.logo} alt={item.show} className="h-4 w-4 rounded-full object-cover" />
            )}
            <span>{item.show}</span>
            <span>·</span>
            <span>{show?.host}</span>
          </div>
          <h2
            className="text-foreground font-bold cursor-pointer hover:text-primary transition-colors"
            style={{ fontSize: 32, lineHeight: "40px", marginBottom: 16 }}
            onClick={onClick}
          >
            {item.title}
          </h2>
          <p
            className="text-foreground/80"
            style={{ fontSize: 18, lineHeight: "28px", marginBottom: 16 }}
          >
            {item.description}
          </p>
          {/* Pills row */}
          <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: 24 }}>
            {pill && (
              <Badge variant="secondary" className={`text-xs ${pill.bg} ${pill.text} ${pill.border}`}>
                {item.category}
              </Badge>
            )}
            {effortStyle && (
              <Badge variant="secondary" className={`text-xs ${effortStyle.className}`}>
                {effortStyle.label}
              </Badge>
            )}
          </div>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              onBuild();
            }}
            className="gap-2 self-start"
            style={{ height: 44, paddingLeft: 24, paddingRight: 24, borderRadius: 6 }}
          >
            Build This Prompt <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
